import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Layout from './Layout';
import apiClient from '../api/apiClient';

const SetupPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const approvedEmail = localStorage.getItem('approved-email');
    const storedName = localStorage.getItem('applicant-name');
    const storedAppId = localStorage.getItem('application-id');
    
    if (!approvedEmail || !storedName || !storedAppId) {
      navigate('/');
      return;
    }
    
    setEmail(approvedEmail);
    setApplicantName(storedName);
    setApplicationId(storedAppId);
  }, [navigate]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasNonalphas = /\W/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasNonalphas) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    if (!confirmPassword.trim()) {
      setError('Please confirm your password');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (process.env.NODE_ENV === 'development') {
      console.log('Setting up password for:', { email: email.trim(), application_id: applicationId });
    }
      
      // Set up password for the approved application
      const response = await apiClient.post('/setup-investor-password', {
        email: email.trim(),
        application_id: applicationId,
        password: password.trim(),
        password_confirmation: confirmPassword.trim(),
      });

              if (process.env.NODE_ENV === 'development') {
          console.log('Password setup response:', response.data);
        }

      if (response.data.status === 'success') {
        const { token } = response.data.data;
        
        // Store authentication token
        localStorage.setItem('registration-token', token);
        
        // Store approved email for AuthGuard
        localStorage.setItem('approved-email', email);
        
        // Also store user info for the subscription form
        if (response.data.data.application) {
          localStorage.setItem('applicant-name', response.data.data.application.name);
          localStorage.setItem('application-id', response.data.data.application.id);
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Password setup successful, navigating to subscription form');
          console.log('Stored token:', token);
          console.log('Stored email:', email);
          console.log('Stored name:', response.data.data.application?.name);
        }
        
        // Small delay to ensure localStorage is set
        setTimeout(() => {
          navigate('/subscription');
        }, 100);
      } else {
        setError(response.data.message || 'Failed to set up password');
      }
    } catch (err) {
      console.error('Password setup error:', err);
      
      if (err.response?.data?.errors) {
        // Handle validation errors
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        setError(errorMessages.join('. '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          p: { xs: 3, md: 4 },
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            textAlign: 'center',
            color: '#1f2937',
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 600,
          }}
        >
          Set Up Your Password
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 4,
            textAlign: 'center',
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}
        >
          Create a secure password to access your investor account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 4, backgroundColor: '#f8fdf8', border: '1px solid #d1fae5' }}>
          <CardContent>
            <Typography variant="h6" color="success.main" sx={{ fontWeight: 600, mb: 2 }}>
              Account Details
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Name:</strong> {applicantName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Email:</strong> {email}
            </Typography>
            <Typography variant="body1">
              <strong>Application ID:</strong> #{applicationId}
            </Typography>
          </CardContent>
        </Card>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                py: 1,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Must be at least 8 characters with uppercase, lowercase, number, and special character"
          />

          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            sx={{
              mb: 4,
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                py: 1,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Re-enter your password to confirm"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Set Password & Continue'
            )}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default SetupPassword; 